import type { FeedbackCollectorConfig } from '@/types/feedbackCollector';
import type { IntercomConfig } from '@/types/intercom';
import fallbackFeedbackConfig from '../../public/config/feedbackCollector.config.json';

let cachedFeedbackCollectorConfig: FeedbackCollectorConfig | null = null;
let cachedIntercomConfig: IntercomConfig | null = null;

export const configService = {
  async getFeedbackCollectorConfig(): Promise<FeedbackCollectorConfig> {
    if (cachedFeedbackCollectorConfig) {
      return cachedFeedbackCollectorConfig;
    }

    // Account token resolution order:
    // 1. VITE_FEEDBACK_COLLECTOR_ACCOUNT_TOKEN environment variable
    // 2. feedbackCollector.config.json file (fetched at runtime below)
    // 3. Bundled fallback from the static config file
    const fallbackAccountToken = fallbackFeedbackConfig.accountToken ?? '';
    const accountTokenFromEnv = import.meta.env?.VITE_FEEDBACK_COLLECTOR_ACCOUNT_TOKEN ?? '';

    const defaultFeedbackCollectorConfig: FeedbackCollectorConfig = {
      baseUrl: 'https://mfe.workspace.accessacloud.com/registry/evo-feature-feedback-collector/1/',
      accountToken: accountTokenFromEnv || fallbackAccountToken,
      apiHost: 'https://api.sentiment.accessacloud.com',
      product: 'Projects',
    };

    try {
      const response = await fetch('/config/feedbackCollector.config.json');
      if (!response.ok) {
        return defaultFeedbackCollectorConfig;
      }

      const configFromFile = await response.json();
      cachedFeedbackCollectorConfig = {
        ...defaultFeedbackCollectorConfig,
        ...configFromFile,
        accountToken: accountTokenFromEnv || configFromFile.accountToken || fallbackAccountToken,
      };
      return cachedFeedbackCollectorConfig!;
    } catch {
      return defaultFeedbackCollectorConfig;
    }
  },

  async getIntercomConfig(): Promise<IntercomConfig> {
    if (cachedIntercomConfig) {
      return cachedIntercomConfig;
    }

    const defaultIntercomConfig: IntercomConfig = { appId: '' };

    try {
      const response = await fetch('/config/intercom.config.json');
      if (!response.ok) {
        return defaultIntercomConfig;
      }

      cachedIntercomConfig = await response.json();
      return cachedIntercomConfig!;
    } catch {
      return defaultIntercomConfig;
    }
  },

  clearCache(): void {
    cachedFeedbackCollectorConfig = null;
    cachedIntercomConfig = null;
  },
};
